import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import Network from '@/Network'
import { getApiErrorMessage, ServiceError } from '@/utils/apiError'
import { useDsAttendanceStore } from '@/pinia-store/useDsAttendanceStore'
import type {
  CheckValidationQueueOrderBy,
  ListCheckValidationQueueParams
} from '@/Network/Types/Requests/CheckValidationRequests'
import type {
  CheckRejectionReason,
  CheckValidationHistoryEvent
} from '@/Network/Types/Responses/CheckValidationResponses'
import { CheckValidationStatus } from '@/Network/Types/Responses/CheckValidationResponses'
import {
  CHECK_VALIDATION_DEFAULT_PERIOD_DAYS,
  CHECK_VALIDATION_DEFAULT_STATUSES,
  CHECK_VALIDATION_LIST_LIMIT
} from '@/constants/check-validation.constants'
import type { CheckValidationPeriodDays } from '@/constants/check-validation.constants'
import {
  mapQueueItemToRow,
  periodPresetToRange
} from '@/modules/company/Approvals/CheckValidation/mappers'
import type { CheckValidationRow } from '@/modules/company/Approvals/CheckValidation/types'

export interface RejectCheckPayload {
  version: number
  reason: CheckRejectionReason
  observation?: string
}

const sameValues = <T>(a: T[], b: T[]): boolean =>
  a.length === b.length && a.every((value) => b.includes(value))

/**
 * Q27 — código e frase que o backend devolve (403) nas decisões desta fila
 * depois que o bloqueio da inserção manual liga. O front lança o MESMO erro
 * antes de bater na rede: é ergonomia, não a trava (a trava é do backend).
 */
export const CHECK_VALIDATION_RETIRED_CODE = 'CHECK_VALIDATION_RETIRED'
export const CHECK_VALIDATION_RETIRED_MESSAGE =
  'Esta fila foi aposentada. Os registros manuais viraram Registro de Atividade.'

const retiredError = (): ServiceError =>
  new ServiceError(CHECK_VALIDATION_RETIRED_MESSAGE, {
    isAxiosError: true,
    response: {
      status: 403,
      data: {
        code: CHECK_VALIDATION_RETIRED_CODE,
        message: CHECK_VALIDATION_RETIRED_MESSAGE,
        statusCode: 403
      }
    }
  })

// Guard de corrida: só a resposta do fetch mais recente escreve estado. Escopo
// de módulo para ser monotônico entre instâncias do store (fresh pinia nos testes).
let _fetchId = 0

/**
 * Fila de avaliação de evidências (aba "Avaliação de Evidências" em /aprovacoes).
 *
 * Filtros e paginação moram aqui (não na view). Sem toast: erro de carregamento
 * vira `loadError` para a view renderizar; ações relançam o `ServiceError` para
 * o componente decidir (`notifyError`) — rule `error-handling.md`.
 */
export const useCheckValidationStore = defineStore('checkValidation', () => {
  const defaultPeriod = () => periodPresetToRange(CHECK_VALIDATION_DEFAULT_PERIOD_DAYS)

  const rows = ref<CheckValidationRow[]>([])
  const total = ref(0)
  const page = ref(1)
  const limit = ref(CHECK_VALIDATION_LIST_LIMIT)

  const search = ref('')
  const statuses = ref<CheckValidationStatus[]>([...CHECK_VALIDATION_DEFAULT_STATUSES])
  const onlyOverdue = ref(false)
  // Preset "Últimos N dias" escolhido na barra; `startDate`/`endDate` são o
  // intervalo derivado dele (o que vai na query). Um `setDateRange` avulso zera
  // o preset (`null`) — o chip da barra é do preset, não do intervalo.
  const periodDays = ref<CheckValidationPeriodDays | null>(CHECK_VALIDATION_DEFAULT_PERIOD_DAYS)
  const startDate = ref<string | null>(defaultPeriod().startDate)
  const endDate = ref<string | null>(defaultPeriod().endDate)
  const orderBy = ref<CheckValidationQueueOrderBy>('oldest_first')
  const excludeCreatedBy = ref(false)
  const healthUnitIds = ref<number[]>([])
  const scheduleIds = ref<number[]>([])

  const isLoading = ref(false)
  const isSubmitting = ref(false)
  // Mensagem do último erro de carregamento da fila (vinda do service), para a
  // view exibir no estado de erro. `hasError` é derivado, não flag solta.
  const loadError = ref<string | null>(null)
  const hasError = computed(() => loadError.value !== null)

  // Evidência que respondeu 404 no download: estado de VISUALIZAÇÃO, sem status
  // no backend. Fica fora das rows porque elas são recriadas a cada fetch.
  const evidenceUnavailableIds = ref<number[]>([])

  const selectedCheckIds = ref<Set<number>>(new Set())

  const sessionApproved = ref(0)
  const sessionRejected = ref(0)
  const sessionTotal = computed(() => sessionApproved.value + sessionRejected.value)

  const history = ref<CheckValidationHistoryEvent[]>([])
  const isHistoryLoading = ref(false)
  const historyError = ref<string | null>(null)

  /**
   * Q27 — a fila está aposentada: leitura (fila, histórico, export) continua,
   * decisão não. A verdade é a MESMA do bloqueio da inserção manual, servida
   * pelo backend em `registrationSettings.manualCheckBlockEnabled`; o store da
   * ds-attendance é resolvido preguiçosamente para não obrigar quem só lê a
   * fila a instanciá-lo.
   */
  const isRetired = computed(() => useDsAttendanceStore().isManualCheckBlocked)

  /** Barra a decisão antes da rede quando a fila está aposentada. */
  function assertNotRetired(): void {
    if (isRetired.value) throw retiredError()
  }

  const pageCount = computed(() => Math.ceil(total.value / limit.value))

  const visibleRows = computed(() => rows.value)

  const hasActiveFilters = computed(() => {
    const period = defaultPeriod()
    const isDefaultPeriod =
      periodDays.value === CHECK_VALIDATION_DEFAULT_PERIOD_DAYS &&
      startDate.value === period.startDate &&
      endDate.value === period.endDate
    return (
      search.value.trim() !== '' ||
      !sameValues(statuses.value, CHECK_VALIDATION_DEFAULT_STATUSES) ||
      onlyOverdue.value ||
      !isDefaultPeriod ||
      healthUnitIds.value.length > 0 ||
      scheduleIds.value.length > 0 ||
      orderBy.value !== 'oldest_first' ||
      excludeCreatedBy.value
    )
  })

  const isEvidenceUnavailable = (checkId: number): boolean =>
    evidenceUnavailableIds.value.includes(checkId)

  const selectableRows = computed(() =>
    rows.value.filter((r) => r.status === CheckValidationStatus.PENDING_REVIEW)
  )

  const selectedCount = computed(() => selectedCheckIds.value.size)

  const isAllSelectableSelected = computed(
    () =>
      selectableRows.value.length > 0 &&
      selectableRows.value.every((r) => selectedCheckIds.value.has(r.checkId))
  )

  function toggleSelection(checkId: number) {
    const next = new Set(selectedCheckIds.value)
    if (next.has(checkId)) next.delete(checkId)
    else next.add(checkId)
    selectedCheckIds.value = next
  }

  function toggleSelectAll() {
    if (isAllSelectableSelected.value) {
      selectedCheckIds.value = new Set()
    } else {
      selectedCheckIds.value = new Set(selectableRows.value.map((r) => r.checkId))
    }
  }

  function clearSelection() {
    selectedCheckIds.value = new Set()
  }

  function isSelected(checkId: number): boolean {
    return selectedCheckIds.value.has(checkId)
  }

  function buildListParams(): ListCheckValidationQueueParams {
    const params: ListCheckValidationQueueParams = {
      limit: limit.value,
      offset: (page.value - 1) * limit.value
    }
    const term = search.value.trim()
    if (term) params.search = term
    if (statuses.value.length > 0) {
      params.statuses = statuses.value.join(',')
    }
    if (onlyOverdue.value) params.onlyOverdue = true
    if (scheduleIds.value.length) params.scheduleIds = scheduleIds.value
    if (healthUnitIds.value.length) params.healthUnitIds = healthUnitIds.value
    if (startDate.value) params.startDate = startDate.value
    if (endDate.value) params.endDate = endDate.value
    if (orderBy.value !== 'oldest_first') params.orderBy = orderBy.value
    if (excludeCreatedBy.value) params.excludeCreatedBy = true
    return params
  }

  async function fetchQueue() {
    const fetchId = ++_fetchId
    try {
      isLoading.value = true
      loadError.value = null
      const response = await Network.checkValidation.listQueue(buildListParams())
      if (fetchId === _fetchId) {
        const now = new Date()
        rows.value = response.items.map((item) => mapQueueItemToRow(item, now))
        total.value = response.total
        clearSelection()
      }
    } catch (error) {
      console.error(error)
      // Resposta obsoleta não interessa: um fetch mais novo já assumiu.
      if (fetchId === _fetchId) {
        loadError.value = getApiErrorMessage(
          error,
          'Não foi possível carregar os registros aguardando validação'
        )
      }
    } finally {
      if (fetchId === _fetchId) {
        isLoading.value = false
      }
    }
  }

  /**
   * Aprova o registro. RELANÇA o erro do service: quem chama decide o toast e,
   * no 409/transição inválida, recarrega a fila (`isCheckValidationStaleRowError`).
   * Não recarrega aqui para o caller controlar a ordem fechar-modal → refetch.
   */
  async function approve(checkId: number, version: number): Promise<void> {
    assertNotRetired()
    try {
      isSubmitting.value = true
      await Network.checkValidation.approve(checkId, { version })
      sessionApproved.value++
    } catch (error) {
      console.error(error)
      throw error
    } finally {
      isSubmitting.value = false
    }
  }

  /** Reverte aprovação (APPROVED → PENDING_REVIEW). Mesmo contrato de `approve`. */
  async function reverse(checkId: number, version: number, justification: string): Promise<void> {
    assertNotRetired()
    try {
      isSubmitting.value = true
      await Network.checkValidation.reverse(checkId, { version, justification })
    } catch (error) {
      console.error(error)
      throw error
    } finally {
      isSubmitting.value = false
    }
  }

  /** Reprova o registro (estado terminal). Mesmo contrato de `approve`. */
  async function reject(checkId: number, payload: RejectCheckPayload): Promise<void> {
    assertNotRetired()
    try {
      isSubmitting.value = true
      await Network.checkValidation.reject(checkId, payload)
      sessionRejected.value++
    } catch (error) {
      console.error(error)
      throw error
    } finally {
      isSubmitting.value = false
    }
  }

  async function bulkApprove(): Promise<{ approved: number; failed: number }> {
    assertNotRetired()
    const selected = rows.value.filter((r) => selectedCheckIds.value.has(r.checkId))
    if (selected.length === 0) return { approved: 0, failed: 0 }
    try {
      isSubmitting.value = true
      const response = await Network.checkValidation.bulkApprove({
        items: selected.map((r) => ({ checkId: r.checkId, version: r.version }))
      })
      const approved = response.results.filter((r) => r.success).length
      const failed = response.results.filter((r) => !r.success).length
      clearSelection()
      sessionApproved.value += approved
      return { approved, failed }
    } catch (error) {
      console.error(error)
      throw error
    } finally {
      isSubmitting.value = false
    }
  }

  /** Histórico do registro para o modal; erro vira `historyError` (inline + retry). */
  async function fetchHistory(checkId: number): Promise<void> {
    try {
      isHistoryLoading.value = true
      historyError.value = null
      history.value = []
      const response = await Network.checkValidation.getHistory(checkId)
      history.value = response.events
    } catch (error) {
      console.error(error)
      historyError.value = getApiErrorMessage(
        error,
        'Não foi possível carregar o histórico do registro'
      )
    } finally {
      isHistoryLoading.value = false
    }
  }

  function clearHistory() {
    history.value = []
    historyError.value = null
  }

  function markEvidenceUnavailable(checkId: number) {
    if (!evidenceUnavailableIds.value.includes(checkId)) {
      evidenceUnavailableIds.value = [...evidenceUnavailableIds.value, checkId]
    }
  }

  function clearEvidenceUnavailable(checkId: number) {
    evidenceUnavailableIds.value = evidenceUnavailableIds.value.filter((id) => id !== checkId)
  }

  async function setSearch(value: string) {
    search.value = value
    page.value = 1
    await fetchQueue()
  }

  async function setStatuses(values: CheckValidationStatus[]) {
    statuses.value = [...values]
    page.value = 1
    await fetchQueue()
  }

  async function setOnlyOverdue(value: boolean) {
    onlyOverdue.value = value
    page.value = 1
    await fetchQueue()
  }

  /** Preset da barra ("Últimos 30/90 dias") → intervalo `yyyy-MM-dd` na query. */
  async function setPeriodDays(days: CheckValidationPeriodDays) {
    const range = periodPresetToRange(days)
    periodDays.value = days
    startDate.value = range.startDate
    endDate.value = range.endDate
    page.value = 1
    await fetchQueue()
  }

  async function setDateRange(start: string | null, end: string | null) {
    periodDays.value = null
    startDate.value = start
    endDate.value = end
    page.value = 1
    await fetchQueue()
  }

  async function setHealthUnitIds(ids: number[]) {
    healthUnitIds.value = [...ids]
    page.value = 1
    await fetchQueue()
  }

  async function setScheduleIds(ids: number[]) {
    scheduleIds.value = [...ids]
    page.value = 1
    await fetchQueue()
  }

  async function setPage(value: number) {
    page.value = value
    await fetchQueue()
  }

  async function setOrderBy(value: CheckValidationQueueOrderBy) {
    orderBy.value = value
    page.value = 1
    await fetchQueue()
  }

  async function setExcludeCreatedBy(value: boolean) {
    excludeCreatedBy.value = value
    page.value = 1
    await fetchQueue()
  }

  function resetFilters() {
    const period = defaultPeriod()
    search.value = ''
    statuses.value = [...CHECK_VALIDATION_DEFAULT_STATUSES]
    onlyOverdue.value = false
    periodDays.value = CHECK_VALIDATION_DEFAULT_PERIOD_DAYS
    startDate.value = period.startDate
    endDate.value = period.endDate
    healthUnitIds.value = []
    scheduleIds.value = []
    orderBy.value = 'oldest_first'
    excludeCreatedBy.value = false
    page.value = 1
  }

  /** Volta aos defaults da aba (Em análise + últimos 30 dias) com UM fetch. */
  async function clearFilters() {
    resetFilters()
    await fetchQueue()
  }

  // $reset manual — obrigatório em stores Composition API (regra do repo).
  function $reset() {
    // Invalida fetches em voo para que não repopulem o estado após o reset.
    _fetchId++
    rows.value = []
    total.value = 0
    limit.value = CHECK_VALIDATION_LIST_LIMIT
    resetFilters()
    isLoading.value = false
    isSubmitting.value = false
    loadError.value = null
    evidenceUnavailableIds.value = []
    selectedCheckIds.value = new Set()
    orderBy.value = 'oldest_first'
    excludeCreatedBy.value = false
    sessionApproved.value = 0
    sessionRejected.value = 0
    history.value = []
    isHistoryLoading.value = false
    historyError.value = null
  }

  return {
    rows,
    total,
    page,
    limit,
    search,
    statuses,
    onlyOverdue,
    periodDays,
    startDate,
    endDate,
    healthUnitIds,
    scheduleIds,
    isLoading,
    isSubmitting,
    loadError,
    hasError,
    evidenceUnavailableIds,
    sessionApproved,
    sessionRejected,
    sessionTotal,
    history,
    isHistoryLoading,
    historyError,
    pageCount,
    visibleRows,
    isRetired,
    hasActiveFilters,
    isEvidenceUnavailable,
    selectedCheckIds,
    selectableRows,
    selectedCount,
    isAllSelectableSelected,
    toggleSelection,
    toggleSelectAll,
    clearSelection,
    isSelected,
    buildListParams,
    fetchQueue,
    approve,
    reverse,
    reject,
    bulkApprove,
    fetchHistory,
    clearHistory,
    markEvidenceUnavailable,
    clearEvidenceUnavailable,
    setSearch,
    setStatuses,
    setOnlyOverdue,
    setPeriodDays,
    setDateRange,
    setHealthUnitIds,
    setScheduleIds,
    setOrderBy,
    setExcludeCreatedBy,
    orderBy,
    excludeCreatedBy,
    setPage,
    clearFilters,
    $reset
  }
})
